import config from "@Config/index";

/**
 * TEMPORARY (SEO inter-linking) — gated by `config.HIDE_BLOG_LINKS`.
 *
 * Unwraps `/blog*` anchor tags inside a CMS/API HTML string so they render as plain text
 * (the visible words are kept, only the `<a>` hyperlink is removed). Applied at specific
 * content render sites (About, FAQs, region description/heading) — see
 * sdlc-docs/hide-blog-links-on-places-pages.
 *
 * Pure and isomorphic (no `window`), operates on a single string, fails open on malformed
 * input. When the flag is off it is a strict no-op.
 */

// absolute URL: [1] = host, [2] = path — e.g. https://staging-amber.com/blog/x
const ABSOLUTE_URL = /^https?:\/\/([^/?#]+)([^?#]*)/i;
// protocol-relative URL: [1] = host, [2] = path — e.g. //amberstudent.com/blog
const PROTOCOL_RELATIVE_URL = /^\/\/([^/?#]+)([^?#]*)/;
// amber-owned hosts across environments: amberstudent.com / .cn, staging-amber.com,
// dev-amber.com, api-local.staging-amber.com, etc. External hosts (google.com/blog) are left
// alone — the goal is to stop inter-linking to *our own* blog, not every "/blog" anywhere.
const AMBER_HOST = /amber/i;
// optional language prefix (xx or xx-YY) then `blog` as a whole path segment.
// Matches /blog, /blog/x, /fr/blog/x — but NOT /blogging-tips (segment boundary required).
const BLOG_PATH = /^(?:\/[a-z]{2}(?:-[a-zA-Z]{2})?)?\/blog(?:[/?#]|$)/i;

/** True when `href` resolves to a `/blog*` internal route. */
export function isBlogHref(rawHref: string): boolean {
  if (!rawHref) return false;
  let path = rawHref.trim().replace(/^\\+/, ""); // drop CMS-escaped leading backslashes
  path = path.replace(/&quot;|&#34;|%22/gi, ""); // defensively drop encoded quotes
  const abs = path.match(ABSOLUTE_URL) || path.match(PROTOCOL_RELATIVE_URL);
  if (abs) {
    // absolute / protocol-relative: only amber-owned hosts count as internal blog links
    if (!AMBER_HOST.test(abs[1])) return false;
    path = abs[2] || "/";
  }
  if (!path.startsWith("/")) return false; // mailto: / tel: / other scheme — leave alone
  return BLOG_PATH.test(path);
}

/**
 * Unwrap every `/blog*` anchor in an HTML string. No-op when the flag is off, when the input
 * is not a non-empty string, or on any parse error (fail-open).
 */
export function stripBlogLinks<T>(html: T): T {
  if (!config.HIDE_BLOG_LINKS) return html;
  if (typeof html !== "string" || html.length === 0) return html;
  try {
    // Declared locally (not module-scope) so the `g`-flag `lastIndex` can never leak between
    // calls and the anchor patterns can only ever be used here, with `.replace()`.
    // CMS sometimes inserts a space after '<' ("< a href") so tags render as text.
    const cmsTagSpace = /<\s+(?=\/?[a-zA-Z])/g;
    // A well-formed anchor with a quoted href + its inner content (anchors don't nest).
    const wellFormedAnchor = /<a\b[^>]*?\bhref\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi;
    // An anchor OPENING tag on its own — for malformed CMS HTML where `</a>` is missing
    // (e.g. `<a href="/blog/x">text</p>`). Browsers auto-close such tags into real links.
    const openingAnchor = /<a\b[^>]*?\bhref\s*=\s*(["'])(.*?)\1[^>]*>/gi;

    let out = html.replace(cmsTagSpace, "<");
    // Pass 1: unwrap well-formed blog anchors, keeping their visible text.
    out = out.replace(wellFormedAnchor, (match, _quote, href, inner) =>
      isBlogHref(href) ? inner : match,
    );
    // Pass 2: drop any leftover *unclosed* blog opening tags (keep the following text).
    // Non-blog openings and the openings of well-formed anchors already handled in pass 1
    // are preserved (well-formed ones no longer exist to match).
    out = out.replace(openingAnchor, (match, _quote, href) => (isBlogHref(href) ? "" : match));
    return out as unknown as T;
  } catch {
    return html; // never break a render over a malformed content string
  }
}

export default stripBlogLinks;
